import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit'
import {
  addNotification,
  markNotification,
  setNotificationsError,
  setNotificationsList,
  setNotificationsPending,
  setNotificationsTotalCount,
  setNotificationsUnreadCount,
  resetNotificationsState,
  undoNotification,
  acceptAllFollowRequests,
} from '@wellesley/host-remote/api/actions'
import { NotificationStatus } from '@wellesley/openapi'
import { NotificationItem } from '../types'

export type NotificationsSliceState = {
  data: {
    userId?: string
    list: NotificationItem[] | null
  }
  count: {
    total?: number
    unread?: number
  }
  isPending: boolean
  error?: unknown
}

const initialState: NotificationsSliceState = {
  data: {
    userId: '',
    list: [],
  },
  count: {},
  isPending: false,
}

const selectState = (state: NotificationsSliceState) => state
const selectError = (state: NotificationsSliceState) => state.error
const selectIsPending = (state: NotificationsSliceState) => state.isPending
const selectTotalCount = (state: NotificationsSliceState) => state.count?.total
const selectUnreadCount = (state: NotificationsSliceState) =>
  state.count?.unread

const selectNotificationsOwner = (state: NotificationsSliceState) =>
  state.data.userId

const selectNotifications = createSelector(
  selectState,
  (state) => state.data.list,
)
const selectUnreadNotifications = createSelector(selectNotifications, (list) =>
  list?.filter((n) => n.status === 'UNREAD'),
)
const selectLastNotification = createSelector(selectNotifications, (n) =>
  n?.at(-1),
)

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    updateNotification: (state, action: PayloadAction<NotificationItem>) => {
      if (!state.data.list) return

      const updatedNotification = action.payload

      state.data.list = state.data.list?.map((n) =>
        n.id === updatedNotification.id ? { ...n, ...updatedNotification } : n,
      )
    },
    readAll: (state) => {
      if (state.data.list) {
        state.data.list = state.data.list?.map((n) => ({
          ...n,
          status: 'READ',
        }))
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setNotificationsPending, (state, action) => {
        state.isPending = action.payload
      })
      .addCase(setNotificationsError, (state, action) => {
        state.error = action.payload
      })
      .addCase(setNotificationsList, (state, action) => {
        const userId = action.payload.userId
        const notifications = action.payload.list

        state.data = {
          userId,
          list: [
            ...(state.data.list || []),
            ...notifications.filter(
              (n) => !state.data.list?.some((item) => item.id === n.id),
            ),
          ],
        }
      })
      .addCase(addNotification, (state, action) => {
        state.count.unread = (state.count.unread || 0) + 1

        if (state.data.list) {
          state.data.list = [action.payload, ...(state.data.list || [])]
        }
      })
      .addCase(markNotification, (state, action) => {
        if (!state.data.list) return

        state.data.list = state.data.list.map((n) =>
          n.id === action.payload.id
            ? { ...n, status: action.payload.status as NotificationStatus }
            : n,
        )
      })
      .addCase(setNotificationsTotalCount, (state, action) => {
        state.count.total = action.payload
      })
      .addCase(setNotificationsUnreadCount, (state, action) => {
        state.count.unread = action.payload
      })
      .addCase(resetNotificationsState, () => initialState)
      .addCase(undoNotification, (state, action) => {
        const id = action.payload
        const isUnread =
          state.data.list?.find((n) => n.id === id)?.status === 'UNREAD'

        if (isUnread && !!state.count.unread) {
          state.count.unread -= 1
        }

        state.data.list = state.data.list?.filter((n) => n.id !== id) || []
      })
      .addCase(acceptAllFollowRequests, (state) => {
        if (!state.data.list) return

        state.data.list = state.data.list.map((n) => {
          if (n.type === 'FOLLOW_REQUEST') {
            return {
              ...n,
              followResponse: 'Accepted',
            }
          }

          return n
        })
      })
  },
  selectors: {
    selectError,
    selectIsPending,
    selectTotalCount,
    selectUnreadCount,
    selectNotifications,
    selectUnreadNotifications,
    selectLastNotification,
    selectNotificationsOwner,
  },
})
